<template>
  <div
    class="card-product-wrapper-custom"
    @mouseenter="pauseAutoplay"
    @mouseleave="startAutoplay"
  >
    <!-- Left Button (Hidden on Mobile) -->
    <button class="slider-btn left-btn" @click="prevSlide" aria-label="Previous Products">
      ‹
    </button>
    
    <!-- Slider Track Window -->
    <div
      class="product-slider-window"
      @touchstart="onTouchStart"
      @touchend="onTouchEnd"
    >
      <div
        class="product-slider-track"
        :style="trackStyle"
      >
        <div
          v-for="(product, idx) in clonedProducts"
          :key="product.id ? product.id + '-' + idx : 'product-' + idx"
          class="product-slide-card"
          :class="{
            'active-slide': idx === currentIndex,
            'inactive-slide': idx !== currentIndex
          }"
          @click="handleCardClick(product, idx)"
        >
          <div class="product-card-glow"></div>
          <div class="product-card-content">
            <!-- Top Section (Logo) -->
            <div class="logo-wrapper mb-3">
              <img
                :src="product.mitra && product.mitra.mitra_profile ? product.mitra.mitra_profile.logo_mitra : placeholderLogo"
                :alt="product.nama_produk"
                class="logo-img"
              />
            </div>
            
            <!-- Product Title -->
            <h2
              class="my-3 product-title"
              :style="{ color: product.status === 'approved' ? '#f7bd3a' : '#EA181C' }"
            >
              {{ product.nama_produk }}
            </h2>
            
            <!-- Middle Section -->
            <p class="text-primary-description font-weight-bold">
              Setoran Awal: <br />
              mulai dari {{ formatRupiah(product.min_initial_deposit) }}
            </p>
            
            <p class="text-primary-description full-description">
              {{ product.description }}
            </p>
            
            <p class="text-category-tag">
              {{ product.sub_product_category ? product.sub_product_category.name : 'Produk Pilihan' }}
            </p>
            
            <!-- Bottom Section (Nisbah/Rate) -->
            <div class="product-card-footer mt-auto pt-3">
              <p class="text-primary-description mb-0 text-nisbah">
                <template v-if="product.nisbah">
                  Nisbah: {{ product.nisbah }} <br />
                  Proyek Bagi Hasil (Equal Rate): {{ $formatPersen(product.equal_rate) }}%
                </template>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Right Button (Hidden on Mobile) -->
    <button class="slider-btn right-btn" @click="nextSlide" aria-label="Next Products">
      ›
    </button>

    <!-- Pagination Dots -->
    <div class="carousel-dots-container">
      <span
        v-for="(product, idx) in displayProducts"
        :key="'dot-' + idx"
        class="carousel-dot"
        :class="{ 'active-dot': idx === activeIndex }"
        @click="goToSlide(idx)"
      ></span>
    </div>
  </div>
</template>

<script>
import Product from "@/api/produk";
import defaultLogo from "../../assets/images/landing_page/img-logo-bprs.png";

const defaultMockProducts = [
  {
    id: 'mock-1',
    nama_produk: "Deposito Syariah Prima",
    min_initial_deposit: 10000000,
    description: "Simpanan investasi berjangka dengan prinsip bagi hasil Mudharabah Muthlaqah, menawarkan imbal hasil optimal, aman, dan diawasi oleh OJK.",
    sub_product_category: { name: "Deposito" },
    product_category: { name: "deposito" },
    status: "approved",
    nisbah: "65 : 35",
    equal_rate: 14.5,
    mitra: {
      mitra_profile: {
        logo_mitra: defaultLogo
      }
    }
  },
  {
    id: 'mock-2',
    nama_produk: "Tabungan Syariah Hasanah",
    min_initial_deposit: 1000000,
    description: "Simpanan tabungan fleksibel berbasis prinsip Mudharabah Muthlaqah yang memberikan imbal hasil optimal serta kemudahan transaksi kapan saja.",
    sub_product_category: { name: "Tabungan" },
    product_category: { name: "tabungan" },
    status: "approved",
    nisbah: "50 : 50",
    equal_rate: 6.5,
    mitra: {
      mitra_profile: {
        logo_mitra: defaultLogo
      }
    }
  },
  {
    id: 'mock-3',
    nama_produk: "Deposito Berjangka Hasanah",
    min_initial_deposit: 5000000,
    description: "Investasi berjangka syariah aman dan terpercaya dengan bagi hasil bulanan kompetitif untuk membantu mewujudkan masa depan berkah.",
    sub_product_category: { name: "Deposito" },
    product_category: { name: "deposito" },
    status: "approved",
    nisbah: "60 : 40",
    equal_rate: 12.0,
    mitra: {
      mitra_profile: {
        logo_mitra: defaultLogo
      }
    }
  },
  {
    id: 'mock-4',
    nama_produk: "Tabungan Rencana Qurban",
    min_initial_deposit: 250000,
    description: "Tabungan rencana persiapan ibadah Qurban berbasis prinsip Wadiah Yad Dhamanah, aman dan bebas biaya administrasi bulanan.",
    sub_product_category: { name: "Tabungan" },
    product_category: { name: "tabungan" },
    status: "approved",
    nisbah: "Wadiah",
    equal_rate: 0,
    mitra: {
      mitra_profile: {
        logo_mitra: defaultLogo
      }
    }
  }
];

export default {
  name: "CardProduct",
  data() {
    return {
      dataProduct: [],
      placeholderLogo: defaultLogo,
      defaultMockProducts,
      currentIndex: 2, // Start at index 2 (P1)
      isTransitioning: true,
      isAnimating: false,
      autoplayTimer: null,
      touchStartX: 0,
      screenWidth: typeof window !== 'undefined' ? window.innerWidth : 1200
    };
  },
  computed: {
    displayProducts() {
      return this.dataProduct.length > 0 ? this.dataProduct : this.defaultMockProducts;
    },
    clonedProducts() {
      const list = this.displayProducts;
      if (list.length === 0) return [];
      if (list.length === 1) {
        return [list[0], list[0], list[0], list[0], list[0]];
      }
      const lastTwo = list.slice(-2);
      const firstTwo = list.slice(0, 2);
      return [...lastTwo, ...list, ...firstTwo];
    },
    activeIndex() {
      const len = this.displayProducts.length;
      if (len === 0) return 0;
      return (this.currentIndex - 2 + len) % len;
    },
    trackStyle() {
      let cardWidth = 400; // default desktop
      let gap = 24;
      
      if (this.screenWidth < 576) {
        cardWidth = 280;
        gap = 16;
      } else if (this.screenWidth < 992) {
        cardWidth = 340;
        gap = 20;
      }
      
      const offset = (cardWidth / 2) + (this.currentIndex * (cardWidth + gap));
      return {
        transform: `translate3d(calc(50% - ${offset}px), 0, 0)`,
        display: 'flex',
        gap: `${gap}px`,
        transition: this.isTransitioning ? 'transform 0.6s cubic-bezier(0.25, 1, 0.5, 1)' : 'none'
      };
    }
  },
  mounted() {
    this.loadData();
    window.addEventListener("resize", this.updateScreenWidth);
    this.startAutoplay();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScreenWidth);
    this.stopAutoplay();
  },
  methods: {
    formatRupiah(value) {
      if (typeof value !== "number") return value;
      return "Rp. " + value.toLocaleString("id-ID");
    },
    handleCardClick(product, idx) {
      if (this.isAnimating) return;

      if (idx !== this.currentIndex) {
        this.isAnimating = true;
        this.isTransitioning = true;
        this.currentIndex = idx;
        
        setTimeout(() => {
          this.handleTransitionEnd();
        }, 600);
        return;
      }

      const category = product.product_category && product.product_category.name
        ? product.product_category.name.toLowerCase()
        : 'deposito';

      // Check if mock card
      const id = product.id;
      if (id.toString().startsWith('mock-')) {
        if (category === 'deposito') {
          this.$router.push('/list-produk-deposito');
        } else {
          this.$router.push('/list-produk-tabungan');
        }
        return;
      }

      if (category === 'deposito') {
        this.$router.push(`/list-produk-deposito-detail?id=${product.id}`);
      } else if (category === 'tabungan') {
        this.$router.push(`/list-produk-tabungan-detail?id=${product.id}`);
      }
    },
    async loadData() {
      try {
        const res = await Product.index();
        this.dataProduct = res.data.data.data || [];
      } catch (err) {
        console.warn("Failed to fetch products, using fallback mockups:", err);
        this.dataProduct = [];
      }
      this.currentIndex = 2; // Real first product
      this.$emit("update-data-exists", true);
    },
    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    },
    nextSlide() {
      if (this.isAnimating || this.displayProducts.length <= 1) return;
      this.isAnimating = true;
      this.isTransitioning = true;
      this.currentIndex++;
      
      setTimeout(() => {
        this.handleTransitionEnd();
      }, 600);
    },
    prevSlide() {
      if (this.isAnimating || this.displayProducts.length <= 1) return;
      this.isAnimating = true;
      this.isTransitioning = true;
      this.currentIndex--;
      
      setTimeout(() => {
        this.handleTransitionEnd();
      }, 600);
    },
    goToSlide(index) {
      if (this.isAnimating) return;
      this.isAnimating = true;
      this.isTransitioning = true;
      this.currentIndex = index + 2;
      
      setTimeout(() => {
        this.handleTransitionEnd();
      }, 600);
    },
    handleTransitionEnd() {
      this.isAnimating = false;
      const len = this.displayProducts.length;
      if (len === 0) return;

      // Safe wrap checks
      if (this.currentIndex >= len + 2) {
        this.isTransitioning = false;
        this.currentIndex = 2;
      } else if (this.currentIndex <= 1) {
        this.isTransitioning = false;
        this.currentIndex = len + 1;
      }
    },
    startAutoplay() {
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        this.nextSlide();
      }, 5000);
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    pauseAutoplay() {
      this.stopAutoplay();
    },
    onTouchStart(e) {
      this.touchStartX = e.changedTouches[0].clientX;
    },
    onTouchEnd(e) {
      const touchEndX = e.changedTouches[0].clientX;
      const diffX = touchEndX - this.touchStartX;
      if (diffX > 50) {
        this.prevSlide();
      } else if (diffX < -50) {
        this.nextSlide();
      }
    }
  }
};
</script>

<style scoped>
.card-product-wrapper-custom {
  position: relative;
  width: 100%;
  padding: 20px 0;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.product-slider-window {
  width: 100%;
  overflow: hidden;
  padding: 30px 0;
  position: relative;
}

.product-slider-track {
  display: flex;
}

.product-slide-card {
  flex: 0 0 auto;
  width: 400px; /* desktop */
  background: rgba(13, 43, 94, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  padding: 35px 24px;
  height: 520px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  z-index: 1;
}

/* Active card styles */
.product-slide-card.active-slide {
  transform: scale(1.08);
  z-index: 5;
  opacity: 1;
  border-color: #f7bd3a;
  background: rgba(13, 43, 94, 0.7);
  box-shadow: 0 20px 40px rgba(247, 189, 58, 0.15), 0 15px 35px rgba(0, 0, 0, 0.4);
}

.product-slide-card.active-slide .product-card-glow {
  opacity: 1;
}

/* Inactive card styles */
.product-slide-card.inactive-slide {
  transform: scale(0.92);
  opacity: 0.5;
  filter: blur(0.5px);
  pointer-events: auto;
}

/* Hover on inactive cards */
.product-slide-card.inactive-slide:hover {
  opacity: 0.8;
  filter: none;
  transform: scale(0.95);
}

/* Glow effect */
.product-card-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 50% 50%, rgba(247, 189, 58, 0.2) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
  z-index: 1;
}

.product-card-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.logo-wrapper {
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-img {
  max-height: 100%;
  max-width: 120px;
  object-fit: contain;
  filter: brightness(1.2);
}

.product-title {
  font-weight: 700;
  font-size: 22px;
  text-align: center;
  margin-bottom: 16px;
  transition: color 0.3s ease;
}

.text-primary-description {
  color: #e2e8f0;
  text-align: center;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 12px;
}

.full-description {
  color: #b3b3b3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  line-height: 1.5;
  max-height: 6em;
  text-align: center;
  margin-bottom: 16px;
}

.text-category-tag {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 4px 12px;
  border-radius: 20px;
  color: #f7bd3a;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  margin: 0 auto;
  display: inline-block;
}

.text-nisbah {
  font-size: 13px;
  color: #f7bd3a;
  font-weight: 600;
}

/* Slider buttons styling */
.slider-btn {
  background: rgba(18, 51, 108, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 2rem;
  width: 50px;
  height: 50px;
  cursor: pointer;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  backdrop-filter: blur(4px);
}

.slider-btn:hover {
  background: #f7bd3a;
  color: #13346c;
  border-color: #f7bd3a;
  transform: translateY(-50%) scale(1.1);
}

.left-btn {
  left: 20px;
}

.right-btn {
  right: 20px;
}

/* Dots pagination */
.carousel-dots-container {
  display: flex;
  gap: 8px;
  margin-top: 24px;
  z-index: 5;
}

.carousel-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.carousel-dot:hover {
  background: rgba(255, 255, 255, 0.6);
}

.carousel-dot.active-dot {
  background: #f7bd3a;
  width: 24px;
  border-radius: 10px;
}

/* Responsive Media Queries */
@media (max-width: 991px) {
  .product-slide-card {
    width: 340px;
    height: 490px;
    padding: 30px 20px;
  }
  .left-btn {
    left: 10px;
  }
  .right-btn {
    right: 10px;
  }
}

@media (max-width: 575px) {
  .product-slide-card {
    width: 280px;
    height: 480px;
    padding: 25px 16px;
  }
  .slider-btn {
    display: none; /* Hide buttons on mobile touch screens */
  }
}
</style>



// WEBPACK FOOTER //
// src/components/landing-page/cardProduct.vue